import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 60000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    token: process.env.REACT_APP_API_TOKEN,
  }
});

// baseURL sin `/admin` (p. ej. `.../v2`). Rutas relativas → `admin/...`; `app/...` (recover) → `admin/app/...`.
function withAdminApiPrefix(url: string | undefined): string | undefined {
  if (url == null || url === '') return url;
  if (/^https?:\/\//i.test(url)) return url;
  const q = url.indexOf('?');
  const pathOnly = q >= 0 ? url.slice(0, q) : url;
  const query = q >= 0 ? url.slice(q) : '';
  const trimmed = pathOnly.replace(/^\/+/, '');

  console.log('withAdminApiPrefix', trimmed, query);

  if (trimmed.startsWith('app/')) return `${trimmed}${query}`;

  return `admin/${trimmed}${query}`;
}

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (typeof config.url === 'string') {
      config.url = withAdminApiPrefix(config.url);
    }

    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  response => {
    return response.data;
  },
  error => {
    const axiosError = error as AxiosError;

    if (axiosError.response) {
      const responseData: unknown = axiosError.response?.data;

      return Promise.reject(responseData);
    }

    return Promise.reject(error);
  },
);

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  return await axiosInstance.get(url, { ...config });
};

// Función fetchJSON para compatibilidad con código existente
const fetchJSON = async (url: string, options: AxiosRequestConfig = {}) => {
  const { method = 'GET', params, data, ...restOptions } = options;

  const config: AxiosRequestConfig = {
    method,
    params,
    data,
    ...restOptions
  };

  const response = await axiosInstance.request({
    url,
    ...config
  });
  return response;
};

// Funciones de conveniencia para cada método HTTP
export const apiGet = async (url: string, params?: any, config?: AxiosRequestConfig) => {
  console.log('apiGet', url, params, config);

  return await axiosInstance.get(url, { params, ...config });
};

export const apiPost = async (url: string, data?: any, config?: AxiosRequestConfig) => {
  return await axiosInstance.post(url, data, config);
};

export const apiPut = async (url: string, data?: any, config?: AxiosRequestConfig) => {
  return await axiosInstance.put(url, data, config);
};

export const apiPatch = async (url: string, data?: any, config?: AxiosRequestConfig) => {
  return await axiosInstance.patch(url, data, config);
};

export const apiDelete = async (url: string, config?: AxiosRequestConfig) => {
  return await axiosInstance.delete(url, config);
};

// Funciones para manejo del token de autenticación
export const setAuthToken = (token: string | null) => {
  if (token) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    axiosInstance.defaults.headers.common.Authorization = undefined;
  }
};

export const clearAuthToken = () => {
  axiosInstance.defaults.headers.common.Authorization = undefined;
};

export const getAuthToken = () => {
  return axiosInstance.defaults.headers.common.Authorization;
};

// Función para configurar headers personalizados
export const setCustomHeaders = (headers: Record<string, string>) => {
  Object.keys(headers).forEach(key => {
    axiosInstance.defaults.headers.common[key] = headers[key];
  });
};

// Función para remover headers personalizados
export const removeCustomHeaders = (headerKeys: string[]) => {
  headerKeys.forEach(key => {
    delete axiosInstance.defaults.headers.common[key];
  });
};

export default fetchJSON;
export { axiosInstance };
